import { orderBy } from "lodash/fp";
import { useLocation, useNavigate, useParams } from "react-router";

import styled from "@mui/material/styles/styled";
import Typography from "@mui/material/Typography";

import type { PersonTalkTimeInfo } from "@app/analytics/types";
import ROUTES from "@app/common/routes";
import { ELLIPSIS_SX } from "@app/common/styles";
import SvgMessageSquare02 from "@app/uui-icons/MessageSquare02";

import Avatar from "@rui/Avatar";
import Menu, { MenuProps, MenuTitle } from "@rui/Menu";
import MenuItem from "@rui/MenuItem";
import Stack, { Row } from "@rui/Stack";

const MAX_NUM_BEFORE_OVERFLOW = 6; // change the padding of the menu based on scrollbar visibility

interface ParticipantsPopoverMenuProps extends MenuProps {
  title: string;
  data: PersonTalkTimeInfo[];
}

const StyledMenuItem = styled(MenuItem)({
  alignItems: "center",
  justifyContent: "space-between",
});

const ParticipantsPopoverMenu = ({
  open,
  anchorEl,
  onClose,
  title,
  data,
}: ParticipantsPopoverMenuProps) => {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const location = useLocation();

  const handleClick = () => {
    if (location.pathname.includes("deep-dive")) return;
    return navigate(ROUTES.meetingReportTabById(sessionId!, "deep-dive"));
  };

  const padding = data.length > MAX_NUM_BEFORE_OVERFLOW ? 1.5 : undefined;
  const sortedTalkTime = orderBy((p) => p.talkTimePercent, "desc", data);

  return (
    <Menu
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      sx={{ maxHeight: "450px", minWidth: "280px", maxWidth: "300px" }}
    >
      <MenuTitle sx={{ pr: padding }}>{title}</MenuTitle>
      <Stack sx={{ pr: padding }}>
        {sortedTalkTime.map((person) => {
          return (
            <StyledMenuItem
              key={person.name}
              disableRipple
              onClick={handleClick}
            >
              <Row
                variant="left"
                spacing={1}
                sx={{ pr: 1, overflow: "hidden" }}
                flexShrink={1}
              >
                <Avatar name={person.name} />
                <Typography
                  variant="bodySecondaryReg"
                  sx={{
                    ...ELLIPSIS_SX,
                    color: "shades.neutral.700",
                  }}
                >
                  {person.name}
                </Typography>
              </Row>
              <Row variant="right" spacing={0.5} sx={{ minWidth: "50px" }}>
                <SvgMessageSquare02
                  sx={{ fontSize: "10px", color: "shades.neutral.300" }}
                />
                <Typography
                  variant="bodySecondaryReg"
                  sx={{ color: "shades.neutral.600" }}
                >
                  {Math.round(person.talkTimePercent)}%
                </Typography>
              </Row>
            </StyledMenuItem>
          );
        })}
      </Stack>
    </Menu>
  );
};

export default ParticipantsPopoverMenu;
